import ClickAwayListener from "@mui/material/ClickAwayListener";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import clsx from "clsx";
import { memo, useEffect, useReducer, useRef, ReactNode, SyntheticEvent, useMemo, useCallback } from "react";
import Autosuggest, { RenderInputComponentProps, RenderSuggestionParams, ChangeEvent } from "react-autosuggest";
import useNavigate from "@fuse/hooks/useNavigate";
import FuseSvgIcon from "../FuseSvgIcon";
import { FuseFlatNavItemType } from "../FuseNavigation/types/FuseNavItemType";
import { buildNavigationSearchIndex, getNavigationSuggestions, FuseSearchSuggestion } from "./fuseSearch.utils";

const Root = styled("div")(({ theme }) => ({
	"& .FuseSearch-container": {
		position: "relative",
	},
	"& .FuseSearch-suggestionsContainerOpen": {
		position: "absolute",
		zIndex: 1,
		marginTop: theme.spacing(),
		left: 0,
		right: 0,
	},
	"& .FuseSearch-suggestion": {
		display: "block",
	},
	"& .FuseSearch-suggestionsList": {
		margin: 0,
		padding: 0,
		listStyleType: "none",
	},
	"& .FuseSearch-input": {
		transition: theme.transitions.create(["background-color"], {
			easing: theme.transitions.easing.easeInOut,
			duration: theme.transitions.duration.short,
		}),
		"&:focus": {
			backgroundColor: theme.vars.palette.background.paper,
		},
	},
}));

interface RenderInputComponentType {
	variant?: "basic" | "standard";
	inputRef?: (node: HTMLInputElement) => void;
	ref?: (node: HTMLInputElement) => void;
	key?: string;
}

function renderInputComponent(props: RenderInputComponentProps) {
	const { variant, ref, inputRef = () => {}, key, ...other } = props as RenderInputComponentType;
	return (
		<div className="relative w-full" key={key}>
			{variant === "basic" ? (
				// Outlined
				<>
					<TextField
						fullWidth
						autoComplete="off"
						slotProps={{
							input: {
								name: "auto-complete-search",
								role: "search",
								inputRef: (node: HTMLInputElement) => {
									ref?.(node);
									inputRef(node);
								},
								classes: {
									input: "FuseSearch-input py-0 px-4 h-9 md:h-9 ltr:pr-9 rtl:pl-9",
									notchedOutline: "rounded-lg",
								},
							},
						}}
						variant="outlined"
						{...other}
					/>
					<FuseSvgIcon
						className="pointer-events-none absolute top-0 h-9 w-9 p-2 ltr:right-0 rtl:left-0"
						color="action"
					>
						lucide:search
					</FuseSvgIcon>
				</>
			) : (
				// Standard
				<TextField
					fullWidth
					slotProps={{
						input: {
							disableUnderline: true,
							inputRef: (node: HTMLInputElement) => {
								ref?.(node);
								inputRef(node);
							},
							classes: {
								input: "FuseSearch-input py-0 px-4 h-9",
							},
						},
					}}
					variant="standard"
					{...other}
				/>
			)}
		</div>
	);
}

function renderSuggestion(suggestion: FuseSearchSuggestion, { query, isHighlighted }: RenderSuggestionParams) {
	const matches = match(suggestion.title, query);
	const parts = parse(suggestion.title, matches);

	return (
		<MenuItem selected={Boolean(isHighlighted)} component="div">
			<ListItemIcon>
				{suggestion.icon ? (
					<FuseSvgIcon>{suggestion.icon}</FuseSvgIcon>
				) : (
					<span className="w-6 text-center text-2xl font-semibold uppercase">{suggestion.title[0]}</span>
				)}
			</ListItemIcon>
			<ListItemText
				primary={parts?.map((part: { text: string; highlight?: boolean }, index: number) =>
					part.highlight ? (
						<span key={index} style={{ fontWeight: 600 }}>
							{part.text}
						</span>
					) : (
						<strong key={index} style={{ fontWeight: 300 }}>
							{part.text}
						</strong>
					),
				)}
				secondary={suggestion.searchContext || suggestion.subtitle || null}
			/>
		</MenuItem>
	);
}

function getSuggestionValue(suggestion: FuseSearchSuggestion) {
	return suggestion.title;
}

interface StateType {
	searchText: string;
	search: boolean;
	navigation: FuseSearchSuggestion[];
	suggestions: FuseSearchSuggestion[];
	noSuggestions: boolean;
	opened: boolean;
}

const initialState: StateType = {
	searchText: "",
	search: false,
	navigation: [],
	suggestions: [],
	noSuggestions: false,
	opened: false,
};

type ActionType =
	| { type: "setSearchText"; value: string }
	| { type: "setNavigation"; data: FuseSearchSuggestion[] }
	| { type: "updateSuggestions"; value: string }
	| { type: "clearSuggestions" }
	| { type: "open" }
	| { type: "close" };

function reducer(state: StateType, action: ActionType): StateType {
	switch (action.type) {
		case "open": {
			return {
				...state,
				opened: true,
			};
		}
		case "close": {
			return {
				...state,
				opened: false,
				searchText: "",
			};
		}
		case "setSearchText": {
			return {
				...state,
				searchText: action.value,
			};
		}
		case "setNavigation": {
			return {
				...state,
				navigation: action.data,
			};
		}
		case "updateSuggestions": {
			const suggestions = getNavigationSuggestions(action.value, state.navigation);
			const isInputBlank = typeof action.value === "string" && action.value.trim() === "";
			const noSuggestions = !isInputBlank && suggestions.length === 0;

			return {
				...state,
				suggestions,
				noSuggestions,
			};
		}
		case "clearSuggestions": {
			return {
				...state,
				suggestions: [],
				noSuggestions: false,
			};
		}
		default: {
			throw new Error();
		}
	}
}

/**
 * Props for FuseSearch component
 */
interface FuseSearchProps {
	className?: string;
	navigation: FuseFlatNavItemType[];
	variant?: "basic" | "full";
	trigger?: ReactNode;
	triggerTooltip?: string;
	placeholder?: string;
	noResults?: string;
}

/**
 * FuseSearch component
 */
function FuseSearch(props: FuseSearchProps) {
	const {
		navigation = [],
		className,
		variant = "full",
		placeholder = "Pesquisar",
		noResults = "Sem resultados...",
		triggerTooltip = "Clique para buscar",
		trigger = (
			<IconButton>
				<FuseSvgIcon>lucide:search</FuseSvgIcon>
			</IconButton>
		),
	} = props;
	const navigate = useNavigate();

	const [state, dispatch] = useReducer(reducer, initialState);

	const suggestionsNode = useRef<HTMLDivElement>(null);
	const popperNode = useRef<HTMLDivElement>(null);
	const buttonNode = useRef(null);
	const navigationIndex = useMemo(() => buildNavigationSearchIndex(navigation), [navigation]);

	useEffect(() => {
		dispatch({
			type: "setNavigation",
			data: navigationIndex,
		});
	}, [navigationIndex]);

	const showSearch = useCallback(() => {
		dispatch({ type: "open" });
	}, []);

	const hideSearch = useCallback(() => {
		dispatch({ type: "close" });
	}, []);

	useEffect(() => {
		if (!state.opened) {
			return undefined;
		}

		function escFunction(event: KeyboardEvent) {
			if (event.key === "Esc" || event.key === "Escape") {
				hideSearch();
			}
		}

		document.addEventListener("keydown", escFunction, false);
		return () => {
			document.removeEventListener("keydown", escFunction, false);
		};
	}, [hideSearch, state.opened]);

	useEffect(() => {
		if (variant !== "full") {
			return undefined;
		}

		function isShortcutBlockedByTarget(target: EventTarget | null): boolean {
			const element = target instanceof HTMLElement ? target : null;
			if (!element) {
				return false;
			}

			const tag = element.tagName;
			const isFormElement = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
			const isEditable = element.isContentEditable || Boolean(element.closest("[contenteditable='true']"));

			return isFormElement || isEditable;
		}

		function handleShortcut(event: KeyboardEvent) {
			const key = event.key.toLowerCase();
			const isShortcut = (event.ctrlKey || event.metaKey) && key === "k";

			if (!isShortcut || isShortcutBlockedByTarget(event.target)) {
				return;
			}

			event.preventDefault();
			showSearch();
		}

		document.addEventListener("keydown", handleShortcut, false);
		return () => {
			document.removeEventListener("keydown", handleShortcut, false);
		};
	}, [showSearch, variant]);

	function handleSuggestionsFetchRequested({ value }: { value: string }) {
		dispatch({
			type: "updateSuggestions",
			value,
		});
	}

	function handleSuggestionSelected(
		event: SyntheticEvent<unknown>,
		{ suggestion }: { suggestion: FuseSearchSuggestion },
	) {
		event.preventDefault();
		event.stopPropagation();

		if (!suggestion.url) {
			return;
		}

		hideSearch();

		navigate(suggestion.url);
	}

	function handleSuggestionsClearRequested() {
		dispatch({
			type: "clearSuggestions",
		});
	}

	function handleChange(_event: SyntheticEvent<HTMLElement>, { newValue }: ChangeEvent) {
		dispatch({
			type: "setSearchText",
			value: newValue,
		});
	}

	function handleClickAway(event: MouseEvent | TouchEvent) {
		if (
			state.opened &&
			(!suggestionsNode.current ||
				!(event.target instanceof Node && suggestionsNode.current.contains(event.target)))
		) {
			hideSearch();
		}
	}

	switch (variant) {
		case "basic": {
			return (
				<div className={clsx("flex w-full items-center", className)} ref={popperNode}>
					<Autosuggest
						renderInputComponent={renderInputComponent}
						highlightFirstSuggestion
						suggestions={state.suggestions}
						onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
						onSuggestionsClearRequested={handleSuggestionsClearRequested}
						onSuggestionSelected={handleSuggestionSelected}
						getSuggestionValue={getSuggestionValue}
						renderSuggestion={renderSuggestion}
						inputProps={{
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							variant,
							placeholder,
							role: "search",
							value: state.searchText,
							onChange: handleChange,
							onFocus: showSearch,
							InputLabelProps: {
								shrink: true,
							},
							autoFocus: false,
						}}
						theme={{
							container: "flex flex-1 w-full",
							suggestionsList: "FuseSearch-suggestionsList",
							suggestion: "FuseSearch-suggestion",
						}}
						renderSuggestionsContainer={(options) => {
							const { containerProps } = options;
							const { key, ...restContainerProps } = containerProps;

							return (
								<Popper
									anchorEl={popperNode.current}
									open={Boolean(options.children) || state.noSuggestions}
									className="z-9999"
								>
									<div ref={suggestionsNode}>
										<Paper
											key={key}
											{...restContainerProps}
											style={{
												width: popperNode.current ? popperNode.current.clientWidth : "",
											}}
											className="overflow-hidden rounded-lg shadow-lg"
										>
											{options.children}
											{state.noSuggestions && (
												<Typography className="px-4 py-3">{noResults}</Typography>
											)}
										</Paper>
									</div>
								</Popper>
							);
						}}
					/>
				</div>
			);
		}
		case "full": {
			return (
				<Root className={clsx("flex", className)}>
					<Tooltip title={triggerTooltip} placement="bottom">
						<div onClick={showSearch} onKeyDown={showSearch} role="button" tabIndex={0} ref={buttonNode}>
							{trigger}
						</div>
					</Tooltip>

					{state.opened && (
						<ClickAwayListener onClickAway={handleClickAway}>
							<Paper className="shadow-0 absolute inset-x-0 top-0 z-9999 h-full" square>
								<div className="flex h-full w-full items-center" ref={popperNode}>
									<Autosuggest
										renderInputComponent={renderInputComponent}
										highlightFirstSuggestion
										suggestions={state.suggestions}
										onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
										onSuggestionsClearRequested={handleSuggestionsClearRequested}
										onSuggestionSelected={handleSuggestionSelected}
										getSuggestionValue={getSuggestionValue}
										renderSuggestion={renderSuggestion}
										inputProps={{
											placeholder,
											value: state.searchText,
											onChange: handleChange,
											// eslint-disable-next-line @typescript-eslint/ban-ts-comment
											// @ts-ignore
											InputLabelProps: {
												shrink: true,
											},
											autoFocus: true,
										}}
										theme={{
											container: "flex flex-1 w-full",
											suggestionsList: "FuseSearch-suggestionsList",
											suggestion: "FuseSearch-suggestion",
										}}
										renderSuggestionsContainer={(options) => {
											const { containerProps } = options;
											const { key, ...restContainerProps } = containerProps;

											return (
												<Popper
													anchorEl={popperNode.current}
													open={Boolean(options.children) || state.noSuggestions}
													className="z-9999"
												>
													<div ref={suggestionsNode}>
														<Paper
															square
															key={key}
															{...restContainerProps}
															className="shadow-lg"
															style={{
																width: popperNode.current
																	? popperNode.current.clientWidth
																	: "auto",
															}}
														>
															{options.children}
															{state.noSuggestions && (
																<Typography className="px-4 py-3">
																	{noResults}
																</Typography>
															)}
														</Paper>
													</div>
												</Popper>
											);
										}}
									/>
									<IconButton onClick={hideSearch} className="mx-2" size="large">
										<FuseSvgIcon>lucide:x</FuseSvgIcon>
									</IconButton>
								</div>
							</Paper>
						</ClickAwayListener>
					)}
				</Root>
			);
		}
		default: {
			return null;
		}
	}
}

export default memo(FuseSearch);
